import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, Edit, Printer } from 'lucide-react';
import { formatarHoras } from '../../utils/formatarHoras';
import { calcularMetricasHorario } from '../../utils/calcularHorarios';
import { getDaysInPeriod, isWeekend, calcularSemanas } from '../../utils/horarioUtils';
import { SECCOES, TIPOS_DIA } from '../../constants/horarioConstants';

/**
 * Modal para visualização de horário mensal
 * - Barra dos dias fica SEMPRE fixa no topo durante scroll
 * - Coluna de colaboradores fica SEMPRE fixa à esquerda durante scroll horizontal
 * - Suporta múltiplas secções
 */
const HorarioVisualizacaoModal = ({
  show,
  viewingHorario,
  cabecalhoEditavel,
  readOnly,
  onClose,
  onEditCabecalho,
  onPrint
}) => {
  if (!show || !viewingHorario) return null;

  const getCorTurno = (turno) => {
    let fundoSeccao = '';
    let textoSeccao = '';
    if (turno.local === 'Café') {
      fundoSeccao = 'bg-orange-200';
      textoSeccao = 'text-orange-900';
    } else if (turno.local === 'Loja Avelal') {
      fundoSeccao = 'bg-blue-200';
      textoSeccao = 'text-blue-900';
    } else if (turno.local === 'Loja Sátão') {
      fundoSeccao = 'bg-green-200';
      textoSeccao = 'text-green-900';
    }
    
    let corFinal = '';
    if (turno.tipo === 'folga') corFinal = 'bg-yellow-300 border-yellow-500 font-bold';
    else if (turno.tipo === 'folga_compensacao') corFinal = 'bg-yellow-300 border-yellow-500 font-bold';
    else if (turno.tipo === 'ferias') corFinal = 'bg-green-200 border-green-400';
    else if (turno.tipo === 'falta') corFinal = 'bg-red-200 border-red-400';
    else if (turno.tipo === 'reuniao') corFinal = 'bg-purple-200 border-purple-400';
    else if (turno.tipo === 'gestao') corFinal = 'bg-orange-300 border-orange-500';
    else if (turno.tipo === 'integracao') corFinal = 'bg-teal-200 border-teal-400';
    else if (turno.tipo === 'baixa') corFinal = 'bg-yellow-200 border-yellow-400';
    else if (turno.tipo === 'troca') corFinal = `${fundoSeccao} border-pink-400`;
    else if (turno.tipo === 'apoio') corFinal = `${fundoSeccao} border-indigo-400`;
    else corFinal = `${fundoSeccao} border-gray-300`;
    
    return { corFinal, textoSeccao };
  };

  const calcularDuracaoTurno = (entrada, saida) => {
    const [h1, m1] = entrada.split(':').map(Number);
    const [h2, m2] = saida.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const horas = minutos / 60;
    const h = Math.floor(horas);
    const m = Math.round((horas - h) * 60);
    return m === 0 ? `${h}h` : `${h}h${m}m`;
  };

  const renderTurnoCell = (turno, idx) => {
    const { corFinal, textoSeccao } = getCorTurno(turno);
    
    return (
      <div key={idx} className={`p-2 rounded border-2 ${corFinal}`}>
        {turno.tipo === 'folga' && <div className="font-bold text-sm">⚠️ FOLGA</div>}
        {turno.tipo === 'folga_compensacao' && <div className="font-bold text-sm">⚠️ FOLGA C. <span className="text-[10px] font-normal opacity-75">(8h)</span></div>}
        {turno.tipo === 'ferias' && <div className="font-bold text-sm">✈️ Férias</div>}
        {turno.tipo === 'falta' && <div className="font-bold text-sm">❌ Falta</div>}
        {turno.tipo === 'reuniao' && <div className="font-bold text-sm">👥 Reunião</div>}
        {turno.tipo === 'gestao' && <div className="font-bold text-sm">📋 Gestão</div>}
        {turno.tipo === 'integracao' && <div className="font-bold text-sm">🎓 Integração</div>}
        {turno.tipo === 'baixa' && <div className="font-bold text-sm">🏥 Baixa</div>}
        {turno.tipo === 'apoio' && <div className="font-bold text-sm">🤝 Apoio</div>}
        {turno.tipo === 'troca' && <div className="font-bold text-sm">🔄 Troca</div>}
        
        {turno.e_troca && (
          <div className="bg-orange-500 text-white font-bold text-xs px-2 py-1 rounded inline-block mb-1">
            🔄 TROCA
          </div>
        )}
        
        {turno.local && ['normal', 'troca', 'apoio', 'reuniao', 'integracao', 'gestao'].includes(turno.tipo) && (
          <div className={`font-bold text-xs ${textoSeccao}`}>{turno.local}</div>
        )}
        
        {turno.entrada && turno.saida ? (
          <>
            <div className="text-xs">
              {turno.tipo === 'apoio' && 'APOIO '}
              {turno.tipo === 'reuniao' && 'REUNIÃO '}
              {turno.tipo === 'integracao' && 'INTEGRAÇÃO '}
              {turno.tipo === 'gestao' && 'GESTÃO '}
              {turno.entrada}-{turno.saida}
            </div>
            <div className="font-semibold text-xs">{calcularDuracaoTurno(turno.entrada, turno.saida)}</div>
          </>
        ) : (
          <>
            {turno.entrada && <div className="text-xs">{turno.entrada}</div>}
            {turno.saida && <div className="text-xs">{turno.saida}</div>}
          </>
        )}
        
        {turno.observacao && <div className="text-xs italic">⇄ {turno.observacao}</div>}
      </div>
    );
  };

  const days = getDaysInPeriod(viewingHorario.periodo_inicio, viewingHorario.periodo_fim);
  const semanasView = calcularSemanas(viewingHorario.periodo_inicio, viewingHorario.periodo_fim);
  const anoRefView = new Date(viewingHorario.periodo_inicio + 'T00:00:00').getFullYear();

  // Juntar TODOS os colaboradores de TODAS as secções
  const todosColaboradores = viewingHorario.colaboradores_horarios || [];

  return (
    <div 
      id="modal-visualizacao" 
      className="fixed inset-0 bg-black/50 z-50 print:static print:bg-white print:p-0 print:m-0"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {/* Modal container */}
      <div 
        className="bg-card m-2 md:m-4 rounded-lg flex flex-col print:m-0 print:rounded-none"
        style={{ 
          flex: 1,
          minHeight: 0,
          maxHeight: 'calc(100vh - 16px)',
          overflow: 'hidden'
        }}
      >
        
        {/* Header - Fixo */}
        <div className="p-3 md:p-4 border-b border-border bg-card flex items-center justify-between" style={{ flexShrink: 0 }}>
          <h2 className="text-sm md:text-lg font-semibold">
            Horário: {new Date(viewingHorario.periodo_inicio).toLocaleDateString('pt-PT')} - {new Date(viewingHorario.periodo_fim).toLocaleDateString('pt-PT')}
          </h2>
          <div className="flex gap-1 md:gap-2 no-print">
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={onEditCabecalho} className="border-blue-500 text-blue-600 text-xs">
                <Edit className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Editar Cabeçalho</span>
                <span className="sm:hidden">Editar</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Info do cabeçalho - Fixo */}
        {cabecalhoEditavel && (
          <div className="p-2 md:p-3 bg-gray-50 border-b text-xs font-mono whitespace-pre-wrap" style={{ flexShrink: 0, maxHeight: '80px', overflow: 'auto' }}>
            {cabecalhoEditavel
              .replace(/\$\{inicio\}/g, new Date(viewingHorario.periodo_inicio).toLocaleDateString('pt-PT'))
              .replace(/\$\{fim\}/g, new Date(viewingHorario.periodo_fim).toLocaleDateString('pt-PT'))
            }
          </div>
        )}

        {/* Tabela com scroll - ÚNICA área com scroll */}
        <div 
          style={{ 
            flex: 1, 
            minHeight: 0, 
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <table 
            style={{
              borderSpacing: 0, 
              borderCollapse: 'collapse', 
              minWidth: '1800px',
              width: '100%',
              fontSize: '11px'
            }}
          >
            {/* CABEÇALHO DOS DIAS - STICKY */}
            <thead style={{ position: 'sticky', top: 0, zIndex: 30 }}>
              <tr>
                <th style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 40,
                  backgroundColor: '#e5e7eb',
                  padding: '8px 6px',
                  fontWeight: 'bold',
                  borderRight: '2px solid #9ca3af',
                  borderBottom: '2px solid #9ca3af',
                  minWidth: '120px',
                  textAlign: 'left',
                  boxShadow: '4px 0 6px rgba(0,0,0,0.15)'
                }}>
                  Colaborador
                </th>
                {days.map(day => (
                  <th 
                    key={day} 
                    style={{
                      backgroundColor: isWeekend(day) ? '#fde68a' : '#dbeafe',
                      minWidth: '90px',
                      padding: '6px 4px',
                      textAlign: 'center',
                      borderRight: '1px solid #d1d5db',
                      borderBottom: '2px solid #9ca3af',
                      fontSize: '10px'
                    }}
                  >
                    {new Date(day + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit' })}
                  </th>
                ))}
                <th style={{backgroundColor: '#bfdbfe', minWidth: '55px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Total H</th>
                <th style={{backgroundColor: '#bbf7d0', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Férias</th>
                <th style={{backgroundColor: '#fecaca', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Faltas</th>
                <th style={{backgroundColor: '#fef08a', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Baixas</th>
                <th style={{backgroundColor: '#fed7aa', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Gestão</th>
                <th style={{backgroundColor: '#a5f3fc', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Apoio</th>
                <th style={{backgroundColor: '#e5e7eb', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Folgas</th>
                <th style={{backgroundColor: '#fed7aa', minWidth: '55px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Banco Ant.</th>
                <th style={{backgroundColor: '#fef08a', minWidth: '55px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Banco H</th>
                {!readOnly && <th style={{backgroundColor: '#e9d5ff', minWidth: '45px', padding: '6px', textAlign: 'center', borderRight: '1px solid #d1d5db', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Manhãs</th>}
                {!readOnly && <th style={{backgroundColor: '#fbcfe8', minWidth: '45px', padding: '6px', textAlign: 'center', borderBottom: '2px solid #9ca3af', fontSize: '9px'}}>Tardes</th>}
              </tr>
            </thead>
            <tbody>
              {todosColaboradores.map(colab => {
                const metricas = calcularMetricasHorario(colab, semanasView, null, anoRefView);
                const {
                  totalHorasNormais: horasNormais,
                  totalHorasFeriado: horasFeriado,
                  totalHorasReunioes: horasReunioes,
                  totalHoras,
                  diasFerias,
                  diasFalta,
                  diasFolga,
                  diasFolgaComp,
                  diasBaixa,
                  diasGestao,
                  turnosApoio,
                  bancoAnterior,
                  bancoTotal: bancoHoras,
                  totalManhas: contadorManhas,
                  totalTardes: contadorTardes
                } = metricas;
                
                const turnosCount = days.reduce((acc, day) => {
                  const turnos = colab.registos_por_dia?.[day] || [];
                  return Math.max(acc, turnos.length);
                }, 1);
                const rowHeight = Math.max(70, turnosCount * 65);
                
                return (
                  <tr key={colab.colaborador_id} style={{borderBottom: '1px solid #e5e7eb', height: `${rowHeight}px`}}>
                    <td style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      backgroundColor: '#ffffff',
                      padding: '6px',
                      fontWeight: '500',
                      borderRight: '2px solid #9ca3af',
                      minWidth: '120px',
                      verticalAlign: 'top',
                      boxShadow: '4px 0 6px rgba(0,0,0,0.15)',
                      fontSize: '11px'
                    }}>
                      {colab.colaborador_nome}
                    </td>
                    {days.map(day => {
                      const turnos = colab.registos_por_dia?.[day] || [];
                      return (
                        <td key={day} style={{
                          padding: '3px',
                          textAlign: 'center',
                          verticalAlign: 'top',
                          minWidth: '90px',
                          backgroundColor: isWeekend(day) ? '#fffbeb' : 'transparent',
                          borderRight: '1px solid #e5e7eb'
                        }}>
                          {turnos.length > 0 ? (
                            <div className="space-y-1">
                              {turnos.map((turno, idx) => renderTurnoCell(turno, idx))}
                            </div>
                          ) : '-'}
                        </td>
                      );
                    })}
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#eff6ff', verticalAlign: 'top'}}>
                      <div style={{fontSize: '12px', fontWeight: 'bold'}}>{formatarHoras(totalHoras.toFixed(2))}</div>
                      <div style={{fontSize: '8px', color: '#666', marginTop: '2px'}}>
                        <div>🕐 {formatarHoras(horasNormais.toFixed(2))}</div>
                        {horasFeriado > 0 && <div style={{color: '#7c3aed'}}>🎉 {formatarHoras(horasFeriado.toFixed(2))}</div>}
                        {horasReunioes > 0 && <div style={{color: '#3b82f6'}}>📋 {formatarHoras(horasReunioes.toFixed(2))}</div>}
                      </div>
                    </td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#f0fdf4'}}>{diasFerias}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fef2f2'}}>{diasFalta}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fefce8'}}>{diasBaixa}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fff7ed'}}>{diasGestao}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#eff6ff'}}>{turnosApoio}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#f9fafb'}}>{diasFolga}{diasFolgaComp > 0 ? ` (${diasFolgaComp}FC)` : ''}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fff7ed'}}>{formatarHoras(bancoAnterior)}</td>
                    <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fefce8'}}>
                      <span style={{fontSize: '11px', fontWeight: 'bold', color: bancoHoras >= 0 ? '#16a34a' : '#dc2626'}}>
                        {formatarHoras(bancoHoras)}
                      </span>
                    </td>
                    {!readOnly && <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#faf5ff'}}>{contadorManhas}</td>}
                    {!readOnly && <td style={{padding: '6px', textAlign: 'center', backgroundColor: '#fdf2f8'}}>{contadorTardes}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer - Fixo */}
        <div className="p-2 md:p-3 border-t border-border bg-card flex justify-between no-print" style={{ flexShrink: 0 }}>
          <Button variant="outline" size="sm" onClick={() => onPrint(viewingHorario, false)} className="border-green-500 text-green-600">
            <Printer className="h-4 w-4 mr-1" />
            Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4 mr-1" />
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HorarioVisualizacaoModal;
